<script setup>
import { computed } from "vue";
import { router } from "@inertiajs/vue3";
import Modal from "@/Components/Modal.vue";
import { formatPrice, getOriginalPrice, getPrice } from "@/helpers";

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    prompt: {
        type: Object,
        default: null,
    },
    paymentMethod: {
        type: String,
        default: "",
    },
});

const emit = defineEmits(["close"]);

const recoveryOffer = computed(() => props.prompt?.recoveryOffer ?? null);

const price = computed(() => getPrice(recoveryOffer.value, props.paymentMethod));
const originalPrice = computed(() => getOriginalPrice(recoveryOffer.value, props.paymentMethod));
const currency = computed(() => recoveryOffer.value?.currency ?? "BRL");
const hasDiscount = computed(() => originalPrice.value > price.value);

const visitRecoveryOffer = () => {
    if (!recoveryOffer.value?.publicId) {
        return;
    }

    const query = Object.fromEntries(new URLSearchParams(window.location.search));

    router.visit(route("checkout.index", {
        offer: recoveryOffer.value.publicId,
        ...query,
    }));
};
</script>

<template>
    <Modal
        :show="show"
        max-width="lg"
        @close="emit('close')"
    >
        <div class="overflow-hidden bg-white">
            <div class="bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 px-6 py-7 text-white sm:px-8">
                <p class="mb-3 inline-flex rounded-full bg-white/20 px-3 py-1 text-xs font-grotesk65 uppercase tracking-wide">
                    {{ $t('checkout.recovery_offer.badge') }}
                </p>

                <h2 class="font-grotesk75 text-3xl leading-tight sm:text-4xl">
                    {{ $t('checkout.recovery_offer.title') }}
                </h2>

                <p class="mt-4 font-grotesk55 text-base text-white/90">
                    {{ $t('checkout.recovery_offer.description') }}
                </p>
            </div>

            <div class="px-6 py-6 sm:px-8">
                <div v-if="recoveryOffer" class="mb-6 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-4">
                    <p class="font-grotesk65 text-sm text-emerald-950">
                        {{ recoveryOffer.name }}
                    </p>

                    <div class="mt-2 flex flex-wrap items-end gap-2">
                        <span
                            v-if="hasDiscount"
                            class="font-grotesk55 text-sm text-gray-500 line-through"
                        >
                            {{ formatPrice(originalPrice, currency) }}
                        </span>

                        <span class="font-grotesk75 text-3xl text-emerald-700">
                            {{ formatPrice(price, currency) }}
                        </span>
                    </div>
                </div>

                <div class="flex flex-col gap-3 sm:flex-row">
                    <button
                        type="button"
                        class="custom-checkout-button w-full rounded-xl bg-emerald-600 px-5 py-4 font-grotesk75 text-base text-white shadow-lg transition hover:scale-[1.01] hover:bg-emerald-700 focus:outline-none focus:ring-4 focus:ring-emerald-200"
                        @click="visitRecoveryOffer"
                    >
                        {{ $t('checkout.recovery_offer.accept') }}
                    </button>

                    <button
                        type="button"
                        class="w-full rounded-xl border border-gray-200 px-5 py-4 font-grotesk65 text-sm text-gray-600 transition hover:bg-gray-50 sm:w-auto"
                        @click="emit('close')"
                    >
                        {{ $t('checkout.recovery_offer.decline') }}
                    </button>
                </div>
            </div>
        </div>
    </Modal>
</template>
